import {
  Connect,
  ConnectCancelEvent,
  ConnectDoneEvent,
  ConnectErrorEvent,
  ConnectEventHandlers,
  ConnectOptions
} from 'connect-web-sdk';
import { useCallback, useEffect, useState } from 'react';
import { ARGYLE_V2_API_URL } from 'src/features/DUP';
import useApi from 'src/hooks/useApi';
import { useArgyleUser } from 'src/hooks/useArgyleUser';
import { SessionApplication, SessionProperty } from 'src/types/api';

export type BankLinking = ReturnType<typeof useBankLinking>;

interface ConnectURL {
  bankLinkingURL: string;
}
interface AccountData {
  id: string;
  name: string;
  accountNumberDisplay: string;
  institution: { name: string; branding: { icon: string } };
}

interface DisconnectAccountResponse {
  success: boolean;
  accountId: string;
  message: string;
}

export interface BankAccount {
  accountId: string;
  accountName: string;
  institutionName: string;
  logoUrl: string;
  accountNumberDisplay: string;
}

export const useBankLinking = ({
  isBankLinkingFFEnabled,
  application,
  property
}: {
  isBankLinkingFFEnabled?: boolean | null;
  application?: SessionApplication;
  property?: SessionProperty;
}) => {
  const [isBankLinkActive, setBankLinkActive] = useState<boolean>(false);
  const [bankAccountsConnected, setBankAccountsConnected] = useState<BankAccount[] | null>(null);
  const { createDetailedUser, argyleDetailedUser } = useArgyleUser(
    application ?? ({} as SessionApplication),
    property ?? ({} as SessionProperty)
  );

  const {
    makeRequest: getConnectURL,
    data: connectURL,
    loading: isLoadingWidget,
    resetData
  } = useApi<ConnectURL>({
    url: `${ARGYLE_V2_API_URL}/bank/connect_url`,
    loadsOnMount: false
  });

  const {
    makeRequest: fetchConnectedAccounts,
    data: accountsData,
    error: accountsError,
    loading: isLoadingBankAccounts
  } = useApi<AccountData[]>({
    url: `${ARGYLE_V2_API_URL}/bank/accounts`,
    loadsOnMount: false
  });

  const {
    makeRequest: onDisconnectAccount,
    data: disconnectResponse,
    loading: isDisconnectingAccount
  } = useApi<DisconnectAccountResponse>({
    url: `${ARGYLE_V2_API_URL}/bank/accounts/`,
    method: 'DELETE',
    loadsOnMount: false
  });

  const getConnectedAccounts = useCallback(() => {
    fetchConnectedAccounts();
  }, [fetchConnectedAccounts]);

  useEffect(() => {
    if (accountsData) {
      const mappedAccounts = accountsData.map((account) => ({
        accountId: account.id,
        accountName: account.name,
        accountNumberDisplay: account.accountNumberDisplay,
        institutionName: account.institution.name,
        logoUrl: account.institution.branding.icon
      }));

      setBankAccountsConnected(mappedAccounts);
    }
  }, [accountsData]);

  const removeDisconnectedAccount = useCallback((accountId: string) => {
    setBankAccountsConnected(
      (prevState) =>
        prevState?.filter((accountConnected) => accountConnected.accountId !== accountId) || []
    );
  }, []);

  useEffect(() => {
    if (
      !isDisconnectingAccount &&
      disconnectResponse?.success &&
      disconnectResponse.success === true
    ) {
      // Remove the account from the accounts connected state after a successful delete from the API
      removeDisconnectedAccount(disconnectResponse.accountId);
    }
  }, [isDisconnectingAccount, disconnectResponse, removeDisconnectedAccount]);

  // call the argyleuser hook to create user
  const openBankLinkingModal = useCallback(() => {
    const connectEventHandlers: ConnectEventHandlers = {
      onDone: (event: ConnectDoneEvent) => {
        setBankLinkActive(false);
        resetData();
        getConnectedAccounts();
      },
      onCancel: (event: ConnectCancelEvent) => {
        setBankLinkActive(false);
      },
      onError: (event: ConnectErrorEvent) => {
        resetData();
      }
    };

    const connectOptions: ConnectOptions = {
      overlay: 'rgba(199,201,199, 0.5)'
    };
    Connect.destroy();
    if (connectURL?.bankLinkingURL) {
      Connect.launch(connectURL.bankLinkingURL, connectEventHandlers, connectOptions);
    }
  }, [connectURL, resetData, getConnectedAccounts]);

  useEffect(() => {
    if (!argyleDetailedUser && isBankLinkActive) {
      createDetailedUser();
    }
  }, [createDetailedUser, argyleDetailedUser, isBankLinkActive]);

  useEffect(() => {
    if (isBankLinkingFFEnabled && !bankAccountsConnected) {
      getConnectedAccounts();
    }
  }, [isBankLinkingFFEnabled, bankAccountsConnected, getConnectedAccounts]);

  useEffect(() => {
    if (isBankLinkActive && !connectURL && argyleDetailedUser) {
      getConnectURL();
    }
  }, [getConnectURL, connectURL, argyleDetailedUser, isBankLinkActive]);

  useEffect(() => {
    if (isBankLinkActive && connectURL) openBankLinkingModal();
  }, [isBankLinkActive, connectURL, openBankLinkingModal]);

  const disconnectAccount = useCallback(
    (accountId: string) => {
      onDisconnectAccount(accountId);
    },
    [onDisconnectAccount]
  );

  return {
    openBankLinkingModal: () => setBankLinkActive(true),
    bankAccountsConnected: bankAccountsConnected,
    connectURL,
    getConnectedAccounts,
    isBankLinkActive,
    isLoadingBankAccounts: isLoadingBankAccounts,
    isLoadingWidget: isLoadingWidget,
    accountsError,
    isDisconnectingAccount: isDisconnectingAccount,
    disconnectAccount
  };
};
